import { userAtom } from '@/atoms/user.atom';
import { ProcessJob } from '@/graphql/graphql';
import { isInternalUser } from '@/lib/email';
import {
  flattenJobParams,
  formatJobParamValue,
  jobParamLabel,
  type JobParamEntry,
  type JobParamTranslate,
} from '@/lib/jobParams';
import { cn } from '@/lib/utils';
import { useAtomValue } from 'jotai';
import { Check, ChevronRight, Copy } from 'lucide-react';
import { FunctionComponent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * "What did this run with?" — the settings a process job was started with,
 * read back out of its `inputJson`.
 *
 * Staff-only (`isInternalUser`): the payload also carries S3 keys and internal
 * ids, and the labels lean on run-dialog wording that assumes you know the
 * feature. The gate is presentational — `inputJson` has always been on the
 * wire for anyone who can see the batch — so this is a convenience for support,
 * not an access control.
 */
const JobParams: FunctionComponent<{ job: Partial<ProcessJob> }> = ({ job }) => {
  // The panel's keys are assembled at runtime (`groundClassification.runElm`,
  // `batch.srid`), so they can't satisfy `TFunction`'s literal-key union.
  const t = useTranslation('project').t as JobParamTranslate;
  const user = useAtomValue(userAtom);
  const [open, setOpen] = useState(false);
  const [showInternals, setShowInternals] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const [copied, setCopied] = useState(false);

  const { params, internals } = useMemo(() => flattenJobParams(job.inputJson), [job.inputJson]);

  if (!isInternalUser(user?.email)) return null;
  if (params.length === 0 && internals.length === 0) return null;

  const handleCopy = () => {
    navigator.clipboard?.writeText(JSON.stringify(job.inputJson, null, 2)).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      () => {},
    );
  };

  return (
    <div className='mt-1 ml-6'>
      <button
        type='button'
        data-testid='job-params-toggle'
        onClick={() => setOpen((v) => !v)}
        className='flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 dark:text-stone-400 dark:hover:text-stone-100'
      >
        <ChevronRight className={cn('size-3.5 transition-transform', open && 'rotate-90')} />
        {t('batch.parameters')}
        <span className='opacity-60'>({params.length})</span>
      </button>

      {open && (
        <div className='mt-1 rounded-md border border-slate-200/70 bg-slate-50/60 px-3 py-2 dark:border-stone-700/70 dark:bg-stone-900/40'>
          <dl className='grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2'>
            {params.map((entry) => (
              <ParamRow key={entry.path} entry={entry} type={job.type} t={t} />
            ))}
            {showInternals &&
              internals.map((entry) => <ParamRow key={entry.path} entry={entry} type={job.type} t={t} />)}
          </dl>

          <div className='mt-2 flex flex-wrap items-center gap-3 border-t border-slate-200/70 pt-2 text-xs dark:border-stone-700/70'>
            {internals.length > 0 && (
              <button
                type='button'
                data-testid='job-internals-toggle'
                onClick={() => setShowInternals((v) => !v)}
                className='text-slate-500 underline-offset-2 hover:underline dark:text-stone-400'
              >
                {showInternals ? t('batch.hideInternals') : t('batch.showInternals')}{' '}
                <span className='opacity-60'>({internals.length})</span>
              </button>
            )}
            <button
              type='button'
              data-testid='job-raw-toggle'
              onClick={() => setShowRaw((v) => !v)}
              className='text-slate-500 underline-offset-2 hover:underline dark:text-stone-400'
            >
              {showRaw ? t('batch.hideRawJson') : t('batch.showRawJson')}
            </button>
            <button
              type='button'
              onClick={handleCopy}
              className='ml-auto flex items-center gap-1 text-slate-500 underline-offset-2 hover:underline dark:text-stone-400'
            >
              {copied ? <Check className='size-3.5 text-emerald-500' /> : <Copy className='size-3.5' />}
              {copied ? t('batch.copiedJson') : t('batch.copyJson')}
            </button>
          </div>

          {showRaw && (
            <pre
              data-testid='job-raw-json'
              className='mt-2 max-h-80 overflow-auto rounded bg-slate-900 p-2 text-[11px] leading-relaxed text-slate-100 dark:bg-black/50'
            >
              {JSON.stringify(job.inputJson, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};

const ParamRow: FunctionComponent<{
  entry: JobParamEntry;
  type: ProcessJob['type'] | undefined;
  t: JobParamTranslate;
}> = ({ entry, type, t }) => (
  <div data-testid='job-param-row' className='flex items-baseline justify-between gap-3 text-xs'>
    <dt className='min-w-0 truncate text-slate-500 dark:text-stone-400' title={entry.path}>
      {jobParamLabel(t, type ?? 'POST', entry)}
    </dt>
    <dd
      title={formatJobParamValue(entry)}
      className={cn(
        'min-w-0 truncate text-right font-medium tabular-nums text-slate-900 dark:text-stone-100',
        entry.value === true && 'text-emerald-600 dark:text-emerald-400',
        entry.value === false && 'text-slate-400 dark:text-stone-500',
      )}
    >
      {formatJobParamValue(entry)}
    </dd>
  </div>
);

export default JobParams;
